import React from 'react';
import { motion } from 'framer-motion';
import { ShoppingBag, ExternalLink, Star, Heart, Sparkles } from 'lucide-react';
import { useSiteConfig } from '../context/SiteConfigContext';
import { useSiteImages } from '../context/SiteImagesContext';
import { usePageMeta } from '../hooks/usePageMeta';

const Boutique = () => {
  usePageMeta({ title: 'Boutique - GR La Saline', description: 'Boutique officielle GR La Saline : justaucorps, accessoires et articles du club.' });
  const siteConfig = useSiteConfig();
  const images = useSiteImages();
  return (
    <div className="bg-black min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={images.boutique_hero}
            alt="Boutique"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black via-black/70 to-black" />
        </div>
        
        <div className="container mx-auto px-6 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mb-8"
            >
              <span className="inline-block px-4 py-2 bg-fuchsia-500/10 border border-fuchsia-500/30 rounded-full text-fuchsia-300 text-sm font-medium tracking-wider">
                Shopping
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-5xl md:text-7xl lg:text-8xl font-bold text-white mb-8 leading-tight"
            >
              Notre <span className="bg-gradient-to-r from-fuchsia-400 via-pink-400 to-fuchsia-500 bg-clip-text text-transparent">Boutique</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-xl md:text-2xl text-white/70 mb-12 leading-relaxed"
            >
              Découvrez nos produits officiels : tenues, accessoires et articles de GR. 
              Soutenez le club tout en étant parfaitement équipé !
            </motion.p>

            {/* Features */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16 max-w-3xl mx-auto"
            >
              <div className="flex flex-col items-center gap-3">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-fuchsia-500/20 to-pink-500/20 flex items-center justify-center">
                  <Star className="text-fuchsia-400" size={28} />
                </div>
                <h3 className="text-white font-semibold">Qualité Premium</h3>
                <p className="text-white/60 text-sm">Produits officiels et certifiés</p>
              </div>
              
              <div className="flex flex-col items-center gap-3">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-fuchsia-500/20 to-pink-500/20 flex items-center justify-center">
                  <Heart className="text-fuchsia-400" size={28} />
                </div>
                <h3 className="text-white font-semibold">Soutien au Club</h3>
                <p className="text-white/60 text-sm">Vos achats aident le club</p>
              </div>
              
              <div className="flex flex-col items-center gap-3">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-fuchsia-500/20 to-pink-500/20 flex items-center justify-center">
                  <Sparkles className="text-fuchsia-400" size={28} />
                </div>
                <h3 className="text-white font-semibold">Équipement Officiel</h3>
                <p className="text-white/60 text-sm">Collection club exclusive</p>
              </div>
            </motion.div>

            {/* CTA Button */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <motion.a
                href={siteConfig.socialLinks.boutique}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05, boxShadow: '0 0 60px rgba(236, 72, 153, 0.5)' }}
                whileTap={{ scale: 0.98 }}
                className="group relative inline-flex items-center gap-4 px-12 py-6 bg-gradient-to-r from-fuchsia-500 to-pink-500 rounded-full text-white font-bold text-xl overflow-hidden shadow-2xl shadow-fuchsia-500/50"
              >
                <span className="relative z-10 flex items-center gap-4">
                  <ShoppingBag size={28} />
                  Accéder à la Boutique
                  <motion.span
                    animate={{ x: [0, 5, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    <ExternalLink size={24} />
                  </motion.span>
                </span>
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-pink-600 to-fuchsia-600"
                  initial={{ x: '100%' }}
                  whileHover={{ x: 0 }}
                  transition={{ duration: 0.3 }}
                />
              </motion.a>
            </motion.div>

            {/* Info Text */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="text-white/50 text-sm mt-8"
            >
              Boutique propulsée par Hello ASSO • Paiement sécurisé
            </motion.p>
          </motion.div>
        </div>

        {/* Animated Background Elements */}
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 90, 0]
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: 'linear'
          }}
          className="absolute top-1/4 left-10 w-96 h-96 bg-fuchsia-500/10 rounded-full blur-3xl"
        />
        <motion.div
          animate={{
            scale: [1, 1.3, 1],
            rotate: [0, -90, 0]
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: 'linear'
          }}
          className="absolute bottom-1/4 right-10 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl"
        />
      </section>
    </div>
  );
};

export default Boutique;
